// stores/products.ts
import { defineStore } from 'pinia'
import type { ShopListResponse, ShopMeta, ShopProduct } from '~~/types/shop'

type ListParams = {
    page?: number
    per_page?: number
    force?: boolean
}

export const useProductsStore = defineStore('products', {
    state: () => ({
        items: [] as ShopProduct[],
        meta: null as ShopMeta | null,
        loading: false,
        error: null as string | null,

        // simple cache by page+locale if you need quick back/forward navigation
        cache: {} as Record<string, ShopListResponse>,
        // quick byId map for PDPs
        byId: {} as Record<number, ShopProduct>,
    }),

    getters: {
        hasMore: (s) =>
            s.meta ? s.meta.current_page < s.meta.last_page : false,
    },

    actions: {
        _key(params: ListParams) {
            const p = params.page ?? 1
            const pp = params.per_page ?? 12
            const loc = ((useNuxtApp() as any).$i18n?.locale?.value as string) || 'en'
            return `loc=${loc}|p=${p}|pp=${pp}`
        },

        async fetchList(params: ListParams = {}) {
            this.loading = true
            this.error = null
            try {
                const { $api } = useNuxtApp()
                const page = params.page ?? 1
                const per_page = params.per_page ?? 12

                const key = this._key({ page, per_page })
                if (!params.force && import.meta.client && this.cache[key]) {
                    const cached = this.cache[key]
                    this.items = cached.data
                    this.meta = cached.meta
                    return cached
                }

                const res = await $api<ShopListResponse>('/api/shop', {
                    params: { page, per_page }, // use params (typed)
                })

                this.items = res.data
                this.meta = res.meta
                this.cache[key] = res

                for (const p of res.data) this.byId[p.product_id] = p

                return res
            } catch (err: any) {
                this.error = err?.data?.message || 'Failed to load products'
                throw err
            } finally {
                this.loading = false
            }
        },
    },
})
